import { z } from "zod";

import {
  TEXT_COMPOSER_PRODUCT_ALIAS_PATTERN,
  TEXT_COMPOSER_PRODUCT_ALIASES,
  transformTextComposerProductMergeTags,
} from "./productAliases";
import {
  DISCOVER_HEADING_TAG,
  hasProductContextMarkers,
  inlineProductMarkers,
} from "./productContexts";
import {
  parseProductItemIdentifier,
  productItemIdentifier,
  productItemLabel,
  PRODUCT_ITEM_TAG_PATTERN,
} from "./productItemTags";

import {
  PRODUCT_GRID_MODELS,
  type ProductGridModelValue,
  MODEL_TO_HEADING,
} from "@/features/EmailEditor/plugins/productGridConstants";
import {
  generatedContentIdentifier,
  getGeneratedContentField,
  hasProductContextTags,
  mergeTagsService,
  MERGE_TAG_PATTERN,
  parseGeneratedContentIdentifier,
  parseMergeTagSlot,
  PRODUCT_FIELDS,
  type MergeTagCategory,
  type MergeTagFilterId,
  type ParsedMergeTagSlot,
  type ResolvedFilters,
} from "@/services/mergeTags";

// Re-export the agnostic serialization primitives so existing Text Composer
// call sites keep importing them from this feature module.
export { MERGE_TAG_PATTERN, parseMergeTagSlot, type ParsedMergeTagSlot };

export const TEXT_COMPOSER_DEFAULT_PRODUCT_MODEL: ProductGridModelValue =
  "magic_items";

export const productGridModelSchema = z.enum(
  PRODUCT_GRID_MODELS.map((model) => model.value) as [
    ProductGridModelValue,
    ...ProductGridModelValue[],
  ],
);

export function normalizeForLabelMatch(value: string): string {
  return value.toLowerCase().replace(/[_-]/g, " ").trim().replace(/\s+/g, " ");
}

/**
 * Dragon-tile category strings whose tile name differs from the product grid
 * model label, keyed by the normalized category (see {@link normalizeForLabelMatch}).
 *
 * A "Cart" parent tile carries category "Cart", but the cart model's label is
 * "Cart Items" — so plain label matching misses it and the tile falls back to
 * `magic_items`. This is the category→model sibling of the model→path special
 * case in `productAliases.ts` (`discover` → `tile_data.dynamic…`). Tiles that
 * also diverge but have no FE model yet (Post Purchase, Product Story) can be
 * added here once those models exist in {@link PRODUCT_GRID_MODELS}.
 */
const DRAGON_TILE_CATEGORY_MODEL_ALIASES: Record<
  string,
  ProductGridModelValue
> = {
  cart: "cart_items",
  // The sale tile's backend category is still "Price Drop" (FOG-2471 renamed
  // only the picker label), so it no longer matches by label.
  "price drop": "price_drop_items",
};

/** True when `category` matches a product grid picker label (case/spacing insensitive). */
export function categoryMatchesProductGridModelLabel(
  category: string | undefined,
): boolean {
  const key = normalizeForLabelMatch(category ?? "");
  if (key.length === 0) return false;
  for (const { label } of PRODUCT_GRID_MODELS) {
    if (normalizeForLabelMatch(label) === key) return true;
  }
  return key in DRAGON_TILE_CATEGORY_MODEL_ALIASES;
}

/**
 * Picks the product grid model for Text Composer merge tags when the supplied
 * category string matches a product grid model label (case/spacing normalized).
 * The string usually comes from {@link innerTileCategoryString} (inner tile label
 * when it is a grid model, else parent dragon tile category).
 */
export function resolveProductGridModelForDragonTileCategory(
  dragonTileCategory?: string,
): ProductGridModelValue {
  const key = normalizeForLabelMatch(dragonTileCategory ?? "");

  if (key.length === 0) {
    return TEXT_COMPOSER_DEFAULT_PRODUCT_MODEL;
  }

  for (const { value, label } of PRODUCT_GRID_MODELS) {
    if (normalizeForLabelMatch(label) === key) {
      return value;
    }
  }

  return (
    DRAGON_TILE_CATEGORY_MODEL_ALIASES[key] ??
    TEXT_COMPOSER_DEFAULT_PRODUCT_MODEL
  );
}

export interface TextComposerMergeTagDefinition {
  label: string;
  tag: string;
  /** Sample value shown inside the Lexical editor chip (e.g. "Jane"). */
  preview: string;
  /** Filters enabled by default when the tag is first inserted (from service catalog). */
  defaultFilters?: ResolvedFilters;
}

export interface TextComposerMergeTagCategory {
  id: string;
  label: string;
  tags: TextComposerMergeTagDefinition[];
}

export const DISCOVER_HEADING_LABEL = "Discover headline";

/** The picker/typeahead entry for `{{heading}}`; offered inside Discover sections only. */
export const DISCOVER_HEADING_PICKER_TAG: TextComposerMergeTagDefinition = {
  label: DISCOVER_HEADING_LABEL,
  tag: `{{${DISCOVER_HEADING_TAG}}}`,
  preview: MODEL_TO_HEADING.discover,
};

/**
 * The picker's product category: the shared product fields as `items.0.field`
 * tags, plus the Discover headline. Insertion resolves the real index from the
 * caret (block, context, or a freshly created context), so one entry per field
 * is enough.
 */
function buildProductItemPickerCategory(): TextComposerMergeTagCategory {
  return {
    id: "product-data",
    label: "Product data",
    tags: [
      // Generic template: no index, no model — the caret resolves both at insert.
      ...PRODUCT_FIELDS.map((field) => ({
        label: `Product ${field.label}`,
        tag: `{{${productItemIdentifier(0, field.field)}}}`,
        preview: field.preview,
      })),
      DISCOVER_HEADING_PICKER_TAG,
    ],
  };
}

function toComposerCategories(
  cats: MergeTagCategory[],
): TextComposerMergeTagCategory[] {
  return cats.map((cat) => ({
    id: cat.id,
    label: cat.label,
    tags: cat.tags.map((def) => ({
      label: def.label,
      tag: `{{${def.identifier}}}`,
      preview: def.preview,
      defaultFilters: def.defaultFilters,
    })),
  }));
}

export function getUrlMergeTagPickerConfig(
  options: { includeProductAliases?: boolean } = {},
): {
  allowedTags: string[];
  extraCategories: MergeTagCategory[];
} {
  const { includeProductAliases = true } = options;
  const serviceUrlIds = mergeTagsService
    .list({ surface: "textComposer", tagType: "url" })
    .flatMap((category) => category.tags.map((tag) => tag.identifier));

  const urlAliases = TEXT_COMPOSER_PRODUCT_ALIASES.filter(
    (alias) => alias.type === "url",
  );
  const pickerAliases = urlAliases.filter((alias) => !alias.hiddenFromPicker);

  const extraCategories: MergeTagCategory[] =
    includeProductAliases && pickerAliases.length > 0
      ? [
          {
            id: "product-data",
            label: "Product data",
            tags: pickerAliases.map((alias) => ({
              identifier: alias.identifier,
              label: alias.label,
              preview: alias.preview,
              categoryId: "product-data",
              availability: "productContext",
              filters: [],
              tagType: "url",
            })),
          },
        ]
      : [];

  return {
    // Hidden legacy aliases (e.g. cart_url) stay out of the picker but remain
    // detectable, so existing drafts still render them as chips.
    allowedTags: [
      ...serviceUrlIds,
      ...urlAliases.map((alias) => alias.identifier),
    ],
    extraCategories,
  };
}

/** Replaces Text Composer organization aliases (e.g. `{{ support_link }}`) with copy merge tags. */
const SUPPORT_LINK_ALIAS_PATTERN = /\{\{\s*support_link\s*\}\}/gi;

function transformTextComposerOrganizationMergeTags(input: string): string {
  return input.replace(SUPPORT_LINK_ALIAS_PATTERN, "{{copy.support_email}}");
}

export function buildTextComposerPickerCategories(
  extraCategories: MergeTagCategory[] = [],
): TextComposerMergeTagCategory[] {
  return [
    // Products first — the most-used tags in this composer.
    buildProductItemPickerCategory(),
    ...toComposerCategories(mergeTagsService.list({ surface: "textComposer" })),
    // Connector merge tags last — there can be many of them.
    ...toComposerCategories(extraCategories),
  ];
}

/** Base picker categories (no connector metadata). Prefer {@link buildTextComposerMergeTagCategories}. */
export const textComposerMergeTagCategories: TextComposerMergeTagCategory[] =
  buildTextComposerPickerCategories();

// ---------------------------------------------------------------------------
// Merge tag registry for Lexical editor integration
// ---------------------------------------------------------------------------

export interface MergeTagRegistryEntry {
  /** The raw identifier inside `{{ }}`, e.g. `customer.first_name`. */
  identifier: string;
  /** Human-readable label, e.g. "First Name". */
  label: string;
  /** Sample preview value shown in the editor chip, e.g. "Jane". */
  preview: string;
}

/**
 * Side registry for connector metadata tags so Lexical insert/auto-detect can
 * resolve identifiers that are not in the static service catalog.
 */
const connectorMergeTagRegistry = new Map<string, MergeTagRegistryEntry>();

// Bumped whenever the connector identifier set actually changes, so editor
// plugins can re-run detection once installed-connector tags load (async).
let connectorRegistrySignature = "";
let connectorRegistryVersion = 0;
const connectorRegistryListeners = new Set<() => void>();

export function subscribeConnectorMergeTagRegistry(
  listener: () => void,
): () => void {
  connectorRegistryListeners.add(listener);
  return () => {
    connectorRegistryListeners.delete(listener);
  };
}

export function getConnectorMergeTagRegistryVersion(): number {
  return connectorRegistryVersion;
}

export function syncConnectorMergeTagRegistry(
  categories: MergeTagCategory[],
): void {
  connectorMergeTagRegistry.clear();
  for (const category of categories) {
    for (const tag of category.tags) {
      connectorMergeTagRegistry.set(tag.identifier, {
        identifier: tag.identifier,
        label: tag.label,
        preview: tag.preview,
      });
    }
  }

  // Notify only on a real change so repeated effect-driven syncs do not
  // re-run every mounted editor's detection transform.
  const signature = Array.from(connectorMergeTagRegistry.keys())
    .sort()
    .join("\n");
  if (signature === connectorRegistrySignature) return;
  connectorRegistrySignature = signature;
  connectorRegistryVersion += 1;
  for (const listener of connectorRegistryListeners) listener();
}

/**
 * Builds Text Composer picker categories including installed-connector
 * customer metadata tags (e.g. `customer.metadata.yotpo__loyalty_tier`).
 */
export function buildTextComposerMergeTagCategories(
  extraCategories: MergeTagCategory[] = [],
): TextComposerMergeTagCategory[] {
  syncConnectorMergeTagRegistry(extraCategories);
  return buildTextComposerPickerCategories(extraCategories);
}
/**
 * True when content contains a product/cart merge tag — a canonical
 * `tile_data.*` / `item.*` / `cart.*` tag (via the agnostic service primitive),
 * a V2 `items.N.field` tag or `{% with items=tile_data.* %}` context marker
 * (the service keywords don't match `items.*`), or a legacy
 * `{{ product_name }}`-style authoring alias.
 */
export function hasProductMergeTag(input?: string): boolean {
  if (!input) return false;
  if (hasProductContextTags(input)) return true;
  PRODUCT_ITEM_TAG_PATTERN.lastIndex = 0;
  if (PRODUCT_ITEM_TAG_PATTERN.test(input)) return true;
  if (hasProductContextMarkers(input)) return true;
  TEXT_COMPOSER_PRODUCT_ALIAS_PATTERN.lastIndex = 0;
  return TEXT_COMPOSER_PRODUCT_ALIAS_PATTERN.test(input);
}

export function hasProductMergeTagsInAuthoringState(state: {
  bodyText?: string;
  ctaButton?: { buttonText?: string; buttonUrl?: string };
}): boolean {
  return (
    hasProductMergeTag(state.bodyText) ||
    hasProductMergeTag(state.ctaButton?.buttonText) ||
    hasProductMergeTag(state.ctaButton?.buttonUrl)
  );
}

function replaceStaticDtlMergeTags(input: string): string {
  return input.replace(MERGE_TAG_PATTERN, (match, inner: string) => {
    const parsed = parseMergeTagSlot(inner);
    if (!parsed) return match;
    const def = mergeTagsService.find(parsed.identifier);
    return def?.staticDtl ?? match;
  });
}

export function transformTextComposerMergeTags(
  input: string,
  productModel: ProductGridModelValue,
): string {
  return replaceStaticDtlMergeTags(
    transformTextComposerOrganizationMergeTags(
      transformTextComposerProductMergeTags(
        // The authoring state's whole-line product markers become inline DTL
        // on the wire — rendered as-is their lines leave stray newlines.
        inlineProductMarkers(input),
        productModel,
      ),
    ),
  );
}

// ---------------------------------------------------------------------------
// Alias registry (product aliases only; connector tags use connectorMergeTagRegistry)
// ---------------------------------------------------------------------------

function buildAliasRegistry(): Map<string, MergeTagRegistryEntry> {
  const map = new Map<string, MergeTagRegistryEntry>();
  // Raw list (not picker categories) so hidden legacy aliases like `cart_url`
  // still resolve a label/preview for chips in existing drafts.
  for (const def of TEXT_COMPOSER_PRODUCT_ALIASES) {
    map.set(def.identifier, {
      identifier: def.identifier,
      label: def.label,
      preview: def.preview,
    });
  }
  return map;
}

const aliasRegistry = buildAliasRegistry();

/**
 * Looks up a merge tag by its identifier (the part inside `{{ }}`).
 * Checks the service catalog first, then connector metadata, then aliases.
 * Returns the label and preview value, or undefined if not recognized.
 */
export function getMergeTagByIdentifier(
  identifier: string | undefined,
  modelLabel?: string,
): MergeTagRegistryEntry | undefined {
  if (identifier == null) return undefined;
  const trimmed = identifier.trim();
  // `items.N.field` is parametric (any index), so it's resolved structurally
  // rather than enumerated in a registry. The returned identifier is the
  // canonical form, so chips normalize whatever spelling was authored.
  // `modelLabel` prefixes the label with the enclosing context's model so the
  // same index reads apart across contexts (see {@link productItemLabel}).
  const item = parseProductItemIdentifier(trimmed);
  if (item) {
    const field = PRODUCT_FIELDS.find((f) => f.field === item.field);
    if (field) {
      return {
        identifier: productItemIdentifier(item.index, item.field),
        label: productItemLabel(item.index, field.label, modelLabel),
        preview: field.preview,
      };
    }
    return undefined;
  }
  // Bound by a discover context's open marker, so it's resolved structurally.
  if (trimmed === DISCOVER_HEADING_TAG) {
    return {
      identifier: DISCOVER_HEADING_TAG,
      label: DISCOVER_HEADING_LABEL,
      preview: MODEL_TO_HEADING.discover,
    };
  }
  // `generated_content.data.<key>` is brand copy bound through a data-variable
  // on the Grapes side, not a catalog tag, so it is resolved structurally here
  // for every input. Chips show the field name; the brand's value lives in the
  // picker row and on the canvas. Only the known keys match, so a typo stays
  // literal text.
  const generatedKey = parseGeneratedContentIdentifier(trimmed);
  if (generatedKey) {
    const label = getGeneratedContentField(generatedKey)?.label ?? generatedKey;
    return {
      identifier: generatedContentIdentifier(generatedKey),
      label,
      preview: label,
    };
  }
  const serviceEntry = mergeTagsService.find(trimmed);
  if (serviceEntry) {
    return {
      identifier: serviceEntry.identifier,
      label: serviceEntry.label,
      preview: serviceEntry.preview,
    };
  }
  const connectorEntry = connectorMergeTagRegistry.get(trimmed);
  if (connectorEntry) return connectorEntry;
  return aliasRegistry.get(trimmed);
}

/** Stable empty reference so chip render memos cache when a tag has no filters. */
const NO_FILTER_IDS: readonly MergeTagFilterId[] = [];

/**
 * Filter ids the chip popover offers for an identifier. Product `items.N.field`
 * chips read their field's filters from `PRODUCT_FIELDS`; everything else
 * comes from the service catalog.
 */
export function getSupportedFilterIds(
  identifier: string | undefined,
): readonly MergeTagFilterId[] {
  if (identifier === undefined) return NO_FILTER_IDS;
  const item = parseProductItemIdentifier(identifier);
  if (item) {
    return (
      PRODUCT_FIELDS.find((f) => f.field === item.field)?.filters ??
      NO_FILTER_IDS
    );
  }
  return mergeTagsService.find(identifier)?.filters ?? NO_FILTER_IDS;
}
